// ============================================================================
// ACTIVITY MASTER SCREEN
// ============================================================================
// Master CRUD screen for basic.tbl_activity via prima-api /api/v1/Activities.
//
// Replicates the EngagementTypeMasterScreen pattern exactly, with additions:
//  - ChargeType dropdown column + filter (loaded from /api/v1/ChargeTypes)
//  - EngagementTypes multi-select in form modal (loaded from /api/v1/EngagementTypes)
//  - loadLookups callback (on mount, save success, handleRestoreModal)

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  ActionMenu,
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  MinimizedModalStrip,
  ColumnSelector,
  type ColumnDefinition,
} from 'components/ui';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import notificationService from 'services/notificationService';
import {
  useActivities,
  useCreateActivity,
  useUpdateActivity,
  useDeleteActivity,
} from 'hooks/api/useActivity';
import { useChargeTypes } from 'hooks/api/useChargeTypes';
import { useEngagementTypes } from 'hooks/api/useEngagementTypes';
import type {
  Activity,
  ActivitySearchRequest,
  CreateActivityRequest,
  UpdateActivityRequest,
} from 'api/types';
import ActivityFormModal, {
  type ActivityFormData,
} from 'components/modals/modules/activity/ActivityFormModal';

type ViewMode = 'list' | 'grid';

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'ACTIVE', label: 'Active only' },
  { value: 'INACTIVE', label: 'Inactive only' },
];

const formatDate = (iso?: string | null): string => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '-';
  }
};

// Column definitions for the ColumnSelector
const columnDefinitions: ColumnDefinition[] = [
  { id: 'code',             label: 'Code',              field: 'code',             required: false, defaultVisible: false, sortable: true },
  { id: 'chargeType',      label: 'Charge Type',       field: 'chargeTypeKey',    required: true,  defaultVisible: true,  sortable: true },
  { id: 'name',            label: 'Activity',          field: 'name',             required: true,  defaultVisible: true,  sortable: true },
  { id: 'engagementTypes', label: 'Engagement Types',  field: 'engagementTypeIds', required: false, defaultVisible: false, sortable: false },
  { id: 'status',          label: 'Status',            field: 'statusName',       required: false, defaultVisible: true,  sortable: true },
  { id: 'createdAt',       label: 'Created',           field: 'createdAt',        required: false, defaultVisible: false, sortable: true },
  { id: 'updatedAt',       label: 'Updated',           field: 'updatedAt',        required: false, defaultVisible: false, sortable: true },
];

const COLUMN_PREF_KEY = 'activity-master-columns';

interface ActivityMasterScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  allowedActions?: string[];
}

const ActivityMasterScreen: React.FC<ActivityMasterScreenProps> = ({
  moduleIcon,
  tabId,
  moduleName,
  allowedActions = [],
}) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  // ----------------------------------------------------------------------------
  // Column visibility state
  // ----------------------------------------------------------------------------
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() =>
    columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id)
  );

  useEffect(() => {
    const saved = localStorage.getItem(COLUMN_PREF_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) setVisibleColumns(parsed);
      } catch { /* ignore */ }
    }
  }, []);

  const handleApplyColumns = useCallback((cols: string[]) => {
    setVisibleColumns(cols);
    localStorage.setItem(COLUMN_PREF_KEY, JSON.stringify(cols));
  }, []);

  const handleResetColumns = useCallback(() => {
    const defaults = columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id);
    setVisibleColumns(defaults);
    localStorage.removeItem(COLUMN_PREF_KEY);
  }, []);

  // ----------------------------------------------------------------------------
  // Lookup data (charge types + engagement types for the form modal)
  // ----------------------------------------------------------------------------
  const { data: chargeTypes, refetch: refetchChargeTypes } = useChargeTypes();
  const { data: engagementTypes, refetch: refetchEngagementTypes } = useEngagementTypes({
    page: 1,
    pageSize: 100,
    includeInactive: false,
  });

  const chargeTypeOptions = useMemo(
    () => chargeTypes.map((ct) => ({ value: ct.id, label: `${ct.code} - ${ct.name}` })),
    [chargeTypes]
  );

  // Map chargeTypeEnumId → chargeTypeKey for conditional validation in the modal
  const chargeTypeKeyMap = useMemo(
    () => Object.fromEntries(chargeTypes.map((ct) => [ct.id, ct.code])),
    [chargeTypes]
  );

  const engagementTypeOptions = useMemo(
    () => engagementTypes.map((et) => ({ value: et.id, label: `${et.code} - ${et.name}` })),
    [engagementTypes]
  );

  // Map engagementType ID → display label for the grid column
  const engagementTypeNameMap = useMemo(
    () => Object.fromEntries(engagementTypes.map((et) => [et.id, et.code])),
    [engagementTypes]
  );

  // Engagement type options for the FILTER panel
  const engagementTypeFilterOptions = useMemo(
    () => [
      { value: '', label: 'All' },
      ...engagementTypes.map((et) => ({ value: et.id, label: `${et.code} - ${et.name}` })),
    ],
    [engagementTypes]
  );

  // Charge type options for the FILTER panel (by key, not UUID)
  const chargeTypeFilterOptions = useMemo(
    () => [
      { value: '', label: 'All' },
      ...chargeTypes.map((ct) => ({ value: ct.code, label: `${ct.code} - ${ct.name}` })),
    ],
    [chargeTypes]
  );

  const loadLookups = useCallback(() => {
    refetchChargeTypes();
    refetchEngagementTypes();
  }, [refetchChargeTypes, refetchEngagementTypes]);

  // ----------------------------------------------------------------------------
  // Server-side query state
  // ----------------------------------------------------------------------------
  const [queryParams, setQueryParams] = useState<ActivitySearchRequest>({
    page: 1,
    pageSize: 25,
    includeInactive: true,
  });
  const { data, pagination, loading, refetch } = useActivities(queryParams);

  // ----------------------------------------------------------------------------
  // View state
  // ----------------------------------------------------------------------------
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  // Filter inputs
  const [codeFilter, setCodeFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [chargeTypeFilter, setChargeTypeFilter] = useState<string>('');
  const [engagementTypeFilter, setEngagementTypeFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Search debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setQueryParams((prev) => ({
        ...prev,
        searchTerm: searchQuery || undefined,
        page: 1,
      }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Pagination
  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams((prev) => ({ ...prev, page: pageIndex + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((size: number) => {
    setQueryParams((prev) => ({ ...prev, pageSize: size, page: 1 }));
  }, []);

  // ----------------------------------------------------------------------------
  // Modal state
  // ----------------------------------------------------------------------------
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selectedRecord, setSelectedRecord] = useState<Activity | null>(null);
  const [isSavingForm, setIsSavingForm] = useState(false);
  const [formApiError, setFormApiError] = useState<string | null>(null);

  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState<string>('0');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const handleRestoreModal = useCallback(
    (restoredMode: 'create' | 'edit', instanceId?: string, recordCode?: string) => {
      loadLookups();
      if (instanceId) setCreateInstanceId(instanceId);
      if (recordCode) {
        const rec = data.find((r) => r.code === recordCode || r.name === recordCode);
        if (rec) setSelectedRecord(rec);
      }
      setFormMode(restoredMode);
      setFormApiError(null);
      setFormModalOpen(true);
    },
    [data, loadLookups]
  );

  // Delete state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Activity | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Mutation hooks
  const { createActivity } = useCreateActivity();
  const { updateActivity } = useUpdateActivity();
  const { deleteActivity } = useDeleteActivity();

  // ----------------------------------------------------------------------------
  // Column definitions for SyutGrid
  // ----------------------------------------------------------------------------
  const tableColumns: SyutGridColumn<Activity>[] = useMemo(
    () => [
      { id: 'code', header: 'Code', accessorKey: 'code', size: 120, enableSorting: true },
      {
        id: 'chargeType',
        header: 'Charge Type',
        accessorKey: 'chargeTypeKey',
        size: 140,
        enableSorting: true,
        cell: ({ row }) => {
          const key = row.original.chargeTypeKey;
          const val = row.original.chargeTypeValue;
          return key ? (
            <span className="badge badge-active" title={val || key}>
              {key}
            </span>
          ) : '-';
        },
      },
      {
        id: 'name',
        header: 'Activity',
        accessorKey: 'name',
        size: 300,
        enableSorting: true,
        cell: ({ row }) => (
          <div
            title={row.original.name}
            style={{
              maxWidth: '280px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {row.original.name}
          </div>
        ),
      },
      {
        id: 'engagementTypes',
        header: 'Engagement Types',
        accessorKey: 'engagementTypeIds',
        size: 200,
        enableSorting: false,
        cell: ({ row }) => {
          const ids = row.original.engagementTypeIds;
          if (!ids || ids.length === 0) return '-';
          const names = ids.map((id) => engagementTypeNameMap[id] || id).join(', ');
          return (
            <span title={names} style={{ fontSize: '0.8125rem' }}>
              {names}
            </span>
          );
        },
      },
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'statusName',
        size: 110,
        enableSorting: true,
        cell: ({ row }) => {
          const isActive = row.original.statusCode === 'ACTIVE';
          return (
            <span className={`badge badge-${isActive ? 'active' : 'inactive'}`}>
              {row.original.statusName || (isActive ? 'Active' : 'Inactive')}
            </span>
          );
        },
      },
      {
        id: 'createdAt',
        header: 'Created',
        accessorKey: 'createdAt',
        size: 130,
        enableSorting: true,
        cell: ({ row }) => formatDate(row.original.createdAt),
      },
      {
        id: 'updatedAt',
        header: 'Updated',
        accessorKey: 'updatedAt',
        size: 130,
        enableSorting: true,
        cell: ({ row }) => formatDate(row.original.updatedAt),
      },
    ],
    [engagementTypeNameMap]
  );

  const visibleTableColumns = useMemo<SyutGridColumn<Activity>[]>(
    () => tableColumns.filter((col) => visibleColumns.includes(col.id as string)),
    [tableColumns, visibleColumns]
  );

  // ----------------------------------------------------------------------------
  // Handlers — open modals
  // ----------------------------------------------------------------------------
  const handleAddNew = useCallback(() => {
    const newId = String(++createInstanceCounter.current);
    setCreateInstanceId(newId);
    setSelectedRecord(null);
    setFormMode('create');
    setFormApiError(null);
    setFormModalOpen(true);
  }, []);

  const handleEdit = useCallback(
    (record: Activity) => {
      const modalId = tabId ? `${tabId}-form-modal-edit-${record.code}` : null;
      if (modalId && isMinimized(modalId)) {
        const modal = getMinimizedModal(modalId);
        dismissMinimized(modalId);
        modal?.onRestore?.();
        return;
      }
      setSelectedRecord(record);
      setFormMode('edit');
      setFormApiError(null);
      setFormModalOpen(true);
    },
    [tabId, isMinimized, dismissMinimized, getMinimizedModal]
  );

  const handleDeleteClick = useCallback((record: Activity) => {
    setDeleteTarget(record);
    setDeleteModalOpen(true);
  }, []);

  // View modal state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewRecord, setViewRecord] = useState<Activity | null>(null);

  const handleView = useCallback((record: Activity) => {
    setViewRecord(record);
    setViewModalOpen(true);
  }, []);

  // ----------------------------------------------------------------------------
  // Save handler
  // ----------------------------------------------------------------------------
  const handleSaveForm = useCallback(
    async (formData: ActivityFormData) => {
      setIsSavingForm(true);
      setFormApiError(null);
      try {
        if (formMode === 'create') {
          const payload: CreateActivityRequest = {
            code: formData.code || undefined,
            name: formData.name,
            chargeTypeEnumId: formData.chargeTypeEnumId,
            engagementTypeIds: formData.engagementTypeIds.length > 0
              ? formData.engagementTypeIds
              : undefined,
            statusCode: formData.statusCode,
          };
          await createActivity(payload);
          notificationService.toast.success(`Activity "${payload.name}" created.`);
        } else if (selectedRecord) {
          const payload: UpdateActivityRequest = {
            code: formData.code || undefined,
            name: formData.name,
            chargeTypeEnumId: formData.chargeTypeEnumId,
            engagementTypeIds: formData.engagementTypeIds.length > 0
              ? formData.engagementTypeIds
              : undefined,
            statusCode: formData.statusCode,
            rowVersion: selectedRecord.rowVersion,
          };
          await updateActivity(selectedRecord.id, payload);
          notificationService.toast.success(`Activity "${payload.name}" updated.`);
        }
        setIsSavingForm(false);
        setFormModalOpen(false);
        setSelectedRecord(null);
        refetch();
        loadLookups();
      } catch (err: any) {
        setIsSavingForm(false);
        const apiBody = err?.response?.data;
        let message = 'Failed to save activity. Please try again.';
        if (typeof apiBody?.message === 'string' && apiBody.message) {
          message = apiBody.message;
        } else if (Array.isArray(apiBody?.errors) && apiBody.errors.length) {
          message = apiBody.errors.join(', ');
        } else if (apiBody?.errors && typeof apiBody.errors === 'object') {
          message = Object.values(apiBody.errors).flat().join(', ');
        } else if (err?.message) {
          message = err.message;
        }
        setFormApiError(message);
        notificationService.toast.error(message, 6000);
      }
    },
    [formMode, selectedRecord, createActivity, updateActivity, refetch, loadLookups]
  );

  // Delete handler
  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deleteActivity(deleteTarget.id);
      notificationService.toast.success(`Activity "${deleteTarget.code}" deleted.`);
      setIsDeleting(false);
      setDeleteModalOpen(false);
      setDeleteTarget(null);
      refetch();
    } catch (err: any) {
      setIsDeleting(false);
      const apiBody = err?.response?.data;
      const message =
        (typeof apiBody?.message === 'string' && apiBody.message) ||
        err?.message ||
        'Failed to delete activity.';
      notificationService.toast.error(message, 6000);
    }
  }, [deleteTarget, deleteActivity, refetch]);

  // ----------------------------------------------------------------------------
  // Filter Apply / Clear
  // ----------------------------------------------------------------------------
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (queryParams.code) count++;
    if (queryParams.name) count++;
    if (queryParams.chargeTypeKey) count++;
    if (queryParams.engagementTypeId) count++;
    if (queryParams.statusCode) count++;
    return count;
  }, [queryParams.code, queryParams.name, queryParams.chargeTypeKey, queryParams.engagementTypeId, queryParams.statusCode]);

  const applyFilters = useCallback(() => {
    setQueryParams((prev) => ({
      ...prev,
      code: codeFilter || undefined,
      name: nameFilter || undefined,
      chargeTypeKey: chargeTypeFilter || undefined,
      engagementTypeId: engagementTypeFilter || undefined,
      statusCode: statusFilter || undefined,
      includeInactive: true,
      page: 1,
    }));
    setFilterOpen(false);
  }, [codeFilter, nameFilter, chargeTypeFilter, engagementTypeFilter, statusFilter]);

  const clearFilters = useCallback(() => {
    setCodeFilter('');
    setNameFilter('');
    setChargeTypeFilter('');
    setEngagementTypeFilter('');
    setStatusFilter('');
    setQueryParams((prev) => ({
      page: 1,
      pageSize: prev.pageSize,
      includeInactive: true,
      statusCode: undefined,
      chargeTypeKey: undefined,
      engagementTypeId: undefined,
      searchTerm: searchQuery || undefined,
    }));
  }, [searchQuery]);

  // ----------------------------------------------------------------------------
  // Row actions
  // ----------------------------------------------------------------------------
  const getRowActions = useCallback(
    (row: Activity): ActionMenuItem[] => {
      const items: ActionMenuItem[] = [];
      if (allowedActions.includes('view')) {
        items.push({
          id: 'view',
          label: 'View',
          icon: <IconSystem.eye size={16} />,
          onClick: () => handleView(row),
        });
      }
      if (allowedActions.includes('edit')) {
        items.push({
          id: 'edit',
          label: 'Edit',
          icon: <IconSystem.edit size={16} />,
          onClick: () => handleEdit(row),
        });
      }
      if (allowedActions.includes('delete')) {
        items.push({
          id: 'delete',
          label: 'Delete',
          icon: <IconSystem.trash size={16} />,
          onClick: () => handleDeleteClick(row),
          variant: 'danger',
        });
      }
      return items;
    },
    [allowedActions, handleView, handleEdit, handleDeleteClick]
  );

  // Action buttons
  const actionButtons: ActionButton[] = useMemo(
    () => [
      ...(allowedActions.includes('add') ? [{
        id: 'add',
        icon: <IconSystem.add size={18} />,
        label: 'Add',
        tooltip: 'Add New Activity',
        onClick: handleAddNew,
        variant: 'icon' as const,
      }] : []),
      {
        id: 'refresh',
        icon: <IconSystem.refresh size={18} />,
        label: 'Refresh',
        tooltip: 'Refresh',
        onClick: () => refetch(),
        variant: 'icon' as const,
      },
    ],
    [allowedActions, handleAddNew, refetch]
  );

  // Form initial data (edit mode)
  const formInitialData: ActivityFormData | null = useMemo(() => {
    if (formMode !== 'edit' || !selectedRecord) return null;
    return {
      code: selectedRecord.code,
      name: selectedRecord.name,
      chargeTypeEnumId: selectedRecord.chargeTypeEnumId ?? '',
      engagementTypeIds: selectedRecord.engagementTypeIds ?? [],
      statusCode: (selectedRecord.statusCode as 'ACTIVE' | 'INACTIVE') ?? 'ACTIVE',
    };
  }, [formMode, selectedRecord]);

  // ----------------------------------------------------------------------------
  // Render
  // ----------------------------------------------------------------------------
  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Activity Master"
        description="Define activities: Statutory Audit, Tax Return Filing, Bookkeeping, etc."
        icon={
          moduleIcon && (IconSystem as any)[moduleIcon]
            ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
            : <IconSystem.file size={18} />
        }
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search code, name, alias…',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: [
            { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List view' },
            { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid view' },
          ],
          onChange: (mode) => setViewMode(mode as ViewMode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen || activeFilterCount > 0,
          activeFilterCount,
        }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        customToolbarItems={
          <ColumnSelector
            columns={columnDefinitions}
            visibleColumns={visibleColumns}
            onApply={handleApplyColumns}
            onReset={handleResetColumns}
          />
        }
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {/* List view */}
            {viewMode === 'list' && (
              <SyutGrid
                data={data}
                columns={visibleTableColumns}
                enableRowSelection
                enablePagination
                manualPagination
                totalCount={pagination.totalItems}
                currentPage={(queryParams.page ?? 1) - 1}
                pageSize={queryParams.pageSize ?? 25}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                enableSorting
                rowActions={
                  (allowedActions.includes('view') || allowedActions.includes('edit') || allowedActions.includes('delete'))
                    ? getRowActions
                    : undefined
                }
                emptyMessage={
                  loading ? 'Loading activities…' : 'No activities found'
                }
              />
            )}

            {/* Grid (card) view */}
            {viewMode === 'grid' && (
              <div
                className="syut-grid-component"
                style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
              >
                <div
                  style={{
                    padding: '1rem',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                    gap: '1.5rem',
                    flex: 1,
                    overflow: 'auto',
                    alignContent: 'start',
                  }}
                >
                  {data.length === 0 && (
                    <div
                      style={{
                        gridColumn: '1 / -1',
                        textAlign: 'center',
                        padding: '2rem',
                        color: 'var(--text-secondary)',
                      }}
                    >
                      {loading ? 'Loading activities…' : 'No activities found'}
                    </div>
                  )}
                  {data.map((item) => {
                    const isActive = item.statusCode === 'ACTIVE';
                    return (
                      <div
                        key={String(item.id)}
                        style={{
                          background: 'var(--bg-primary)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '8px',
                          padding: '1rem',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.5rem',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'start',
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                color: 'var(--text-secondary)',
                                marginBottom: '0.25rem',
                              }}
                            >
                              {item.code}
                            </div>
                            <h3
                              title={item.name}
                              style={{
                                fontSize: '1rem',
                                fontWeight: 700,
                                color: 'var(--text-primary)',
                                margin: 0,
                                marginBottom: '0.5rem',
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                lineHeight: 1.4,
                              }}
                            >
                              {item.name}
                            </h3>
                          </div>
                          <ActionMenu items={getRowActions(item)} />
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: 'auto' }}>
                          {item.chargeTypeKey && (
                            <span className="badge badge-active" title={item.chargeTypeValue || ''}>
                              {item.chargeTypeKey}
                            </span>
                          )}
                          <span className={`badge badge-${isActive ? 'active' : 'inactive'}`}>
                            {item.statusName || (isActive ? 'Active' : 'Inactive')}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Filter panel */}
        {filterOpen && (
        <div className="search-panel">
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
            <button
              className="search-panel-close"
              onClick={() => setFilterOpen(false)}
              title="Close filters"
            >
              <IconSystem.x size={14} />
            </button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Code</label>
              <input
                type="text"
                className="form-input"
                value={codeFilter}
                onChange={(e) => setCodeFilter(e.target.value.toUpperCase())}
                placeholder="Filter by code…"
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Activity Name</label>
              <input
                type="text"
                className="form-input"
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                placeholder="Filter by name…"
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Charge Type"
                options={chargeTypeFilterOptions}
                value={chargeTypeFilter}
                onChange={(val) => setChargeTypeFilter((val as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Engagement Type"
                options={engagementTypeFilterOptions}
                value={engagementTypeFilter}
                onChange={(val) => setEngagementTypeFilter((val as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Status"
                options={STATUS_FILTER_OPTIONS}
                value={statusFilter}
                onChange={(val) => setStatusFilter((val as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
            <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
          </div>
        </div>
        )}
      </div>

      {/* Form modal */}
      <ActivityFormModal
        isOpen={formModalOpen}
        mode={formMode}
        initialData={formInitialData}
        isSaving={isSavingForm}
        apiError={formApiError}
        onSave={handleSaveForm}
        onClose={() => {
          setFormModalOpen(false);
          setSelectedRecord(null);
          setFormApiError(null);
        }}
        chargeTypeOptions={chargeTypeOptions}
        engagementTypeOptions={engagementTypeOptions}
        chargeTypeKeyMap={chargeTypeKeyMap}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreModal={handleRestoreModal}
        createInstanceId={createInstanceId}
        minimizeRef={formModalMinimizeRef}
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        open={deleteModalOpen}
        title="Delete Activity"
        message={
          deleteTarget
            ? `Are you sure you want to delete "${deleteTarget.name}"${deleteTarget.code ? ` (${deleteTarget.code})` : ''}? This action cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting…"
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          if (!isDeleting) {
            setDeleteModalOpen(false);
            setDeleteTarget(null);
          }
        }}
      />

      {viewModalOpen && viewRecord && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-container modal-md">
            <div className="modal-header" style={{ minHeight: 'auto' }}>
              <h2 className="modal-title">View Activity — {viewRecord.name}</h2>
              <button className="modal-close" onClick={() => { setViewModalOpen(false); setViewRecord(null); }} title="Close">
                <IconSystem.x size={20} />
              </button>
            </div>
            <div className="modal-content" style={{ fontSize: '0.8125rem' }}>
              <div className="form-grid-2">
                <div>
                  <label className="form-label">Code</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.code || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Activity</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.name || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Charge Type</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.chargeTypeKey ? `${viewRecord.chargeTypeKey}${viewRecord.chargeTypeValue ? ` — ${viewRecord.chargeTypeValue}` : ''}` : '—'}</div>
                </div>
                <div>
                  <label className="form-label">Engagement Types</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.engagementTypeIds && viewRecord.engagementTypeIds.length > 0 ? viewRecord.engagementTypeIds.map((id) => engagementTypeNameMap[id] || id).join(', ') : '—'}</div>
                </div>
                <div>
                  <label className="form-label">Status</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>
                    <span className={`badge badge-${viewRecord.statusCode === 'ACTIVE' ? 'active' : 'inactive'}`}>{viewRecord.statusName}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer" style={{ minHeight: 'auto' }}>
              <button className="btn btn-secondary" onClick={() => { setViewModalOpen(false); setViewRecord(null); }}>Close</button>
              {allowedActions.includes('edit') && (
                <button className="btn btn-primary" onClick={() => { setViewModalOpen(false); setViewRecord(null); handleEdit(viewRecord); }}>Edit</button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ActivityMasterScreen;
